import React, { useState } from "react";
import { Container, Modal, ModalBody, Nav, NavItem, NavLink } from "reactstrap";
import { getTier } from "./utils/professorTiers";

import ProfessorStatsHeader from "./components/ProfessorStatsHeader";
import ProfessorMetricsList from "./components/ProfessorMetricsList";
import ProfessorHistoryChart from "./components/ProfessorHistoryChart";
import ProfessorExpiringStudents from "./components/ProfessorExpiringStudents";

const ProfessorDetail = ({ professor, onBack }) => {
    const [infoOpen, setInfoOpen] = useState(false);
    const [activeView, setActiveView] = useState('metrics'); // 'metrics' ou 'history'

    if (!professor) return null;

    const finalTier = getTier(professor.scoreFinal);

    const tabStyle = (isActive) => ({
        cursor: "pointer",
        transition: "all 0.3s",
        background: isActive ? "rgba(255,255,255,0.1)" : "transparent",
        color: isActive ? "#fff" : "rgba(255,255,255,0.4)",
        boxShadow: isActive ? "0 2px 10px rgba(0,0,0,0.2)" : "none",
        border: isActive ? "1px solid rgba(255,255,255,0.12)" : "1px solid transparent"
    });

    return (
        <div className="d-flex flex-column h-100 w-100 position-relative" style={{ zIndex: 10, minHeight: "100vh" }}>

            {/* Header */}
            <ProfessorStatsHeader
                professor={professor}
                finalTier={finalTier}
                onBack={onBack}
                setInfoOpen={setInfoOpen}
            />

            {/* Tabs + Content */}
            <Container className="py-3 py-md-4 position-relative" style={{ zIndex: 2, maxWidth: "900px" }}>

                {/* TABS DESKTOP */}
                <Nav pills className="nav-justified rounded-pill p-1 mx-2 mb-4 d-none d-md-flex"
                    style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <NavItem>
                        <NavLink
                            style={tabStyle(activeView === 'metrics')}
                            className="fw-bold rounded-pill py-3 font-size-16 d-flex align-items-center justify-content-center"
                            onClick={() => setActiveView('metrics')}
                        >
                            <i className="mdi mdi-clipboard-list-outline me-2"></i> Relatório Atual
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            style={tabStyle(activeView === 'history')}
                            className="fw-bold rounded-pill py-3 font-size-16 d-flex align-items-center justify-content-center"
                            onClick={() => setActiveView('history')}
                        >
                            <i className="mdi mdi-chart-areaspline me-2"></i> Evolução Temporal
                        </NavLink>
                    </NavItem>
                </Nav>

                {/* TABS MOBILE */}
                <Nav pills className="flex-row nav-justified rounded-pill p-1 mb-4 d-flex d-md-none w-100"
                    style={{ backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <NavItem style={{ flex: '1 1 0' }}>
                        <NavLink
                            style={{ ...tabStyle(activeView === 'metrics'), fontSize: '13px', padding: '12px 4px' }}
                            className="fw-bold rounded-pill d-flex align-items-center justify-content-center text-center h-100"
                            onClick={() => setActiveView('metrics')}
                        >
                            Relatório
                        </NavLink>
                    </NavItem>
                    <NavItem style={{ flex: '1 1 0' }}>
                        <NavLink
                            style={{ ...tabStyle(activeView === 'history'), fontSize: '13px', padding: '12px 4px' }}
                            className="fw-bold rounded-pill d-flex align-items-center justify-content-center text-center h-100"
                            onClick={() => setActiveView('history')}
                        >
                            Evolução
                        </NavLink>
                    </NavItem>
                </Nav>

                {activeView === 'metrics' ? (
                    <>
                        <ProfessorMetricsList professor={professor} />
                        <ProfessorExpiringStudents professor={professor} />
                    </>
                ) : (
                    <ProfessorHistoryChart professor={professor} />
                )}
            </Container>

            {/* Modal de Informações */}
            <Modal isOpen={infoOpen} toggle={() => setInfoOpen(false)} centered size="md"
                contentClassName="border-0 shadow-lg" style={{ borderRadius: "16px", overflow: "hidden" }}>
                <div style={{ background: "linear-gradient(135deg, #0a1628, #1a2a4a)", color: "#fff", padding: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h5 className="mb-0 fw-bold text-white"><i className="mdi mdi-seal me-2"></i>Como funciona o Ranking?</h5>
                    <button onClick={() => setInfoOpen(false)} className="btn btn-sm btn-link p-0 m-0" style={{ color: "rgba(255,255,255,0.4)" }}>
                        <i className="mdi mdi-close font-size-24"></i>
                    </button>
                </div>
                <ModalBody className="p-4" style={{ background: "#0d1b2e" }}>
                    <p className="font-size-15 mb-4" style={{ color: "rgba(255,255,255,0.7)" }}>
                        O nível do instrutor é definido pela nota geral de <strong className="text-white">Performance (0 a 100%)</strong>.
                        A nota é composta pelas metas de qualidade, crescimento da turma e avaliações realizadas no período.
                    </p>

                    <h6 className="fw-bold text-uppercase mb-3 font-size-12" style={{ color: "rgba(255,255,255,0.4)", letterSpacing: "2px" }}>Graduações</h6>

                    <div className="d-flex flex-column gap-2 mb-4">
                        {[
                            { label: "Red", color: "#dc3545", range: "85% – 100%" },
                            { label: "Black", color: "#212529", range: "70% – 84.9%", border: "rgba(255,255,255,0.2)" },
                            { label: "Purple", color: "#6f42c1", range: "55% – 69.9%" },
                            { label: "Blue", color: "#0d6efd", range: "40% – 54.9%" },
                            { label: "White", color: "#e9ecef", range: "Abaixo de 40%", textDark: true },
                        ].map(tier => (
                            <div key={tier.label} className="d-flex align-items-center justify-content-between p-2 px-3 rounded-pill"
                                style={{ backgroundColor: `${tier.color}22`, border: `1px solid ${tier.border || tier.color}33` }}>
                                <span className="fw-bold d-flex align-items-center gap-2" style={{ color: tier.textDark ? "#adb5bd" : tier.color }}>
                                    <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: tier.color }}></div>
                                    {tier.label}
                                </span>
                                <span className="font-size-13" style={{ color: "rgba(255,255,255,0.4)" }}>{tier.range}</span>
                            </div>
                        ))}
                    </div>

                    <div className="p-3 rounded-3 d-flex align-items-start font-size-13"
                        style={{ background: "rgba(241,180,76,0.1)", border: "1px solid rgba(241,180,76,0.2)" }}>
                        <i className="mdi mdi-information font-size-18 me-2 mt-1" style={{ color: "#f1b44c" }}></i>
                        <span style={{ color: "rgba(255,255,255,0.6)" }}>
                            Os <strong className="text-white">Graus (G1 a G3)</strong> indicam o nível de evolução dentro de uma mesma cor de graduação.
                        </span>
                    </div>

                    <button className="btn w-100 mt-4 rounded-pill py-2" onClick={() => setInfoOpen(false)}
                        style={{ background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}>
                        Entendi
                    </button>
                </ModalBody>
            </Modal>

        </div>
    );
};

export default ProfessorDetail;
